/**
 * Компонент второго шага - форма заполнения полей заказа
 *
 * Динамически рендерит поля в зависимости от выбранного источника:
 * - text: текстовое поле
 * - tel: поле для телефона
 * - promo: поле промокода (автоматический uppercase)
 * - buttons: группа кнопок-переключателей
 * - select: выпадающий список (например, для выбора сотрудника)
 *
 * Поддерживает условное отображение полей через showIf
 */
import React from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { SOURCE_FIELDS, ORDER_SOURCES, SourceField } from '../../../config/sourceFields';
import { FieldsFormStepProps } from '../types';

/**
 * Компонент отдельного поля формы
 */
interface FormFieldProps {
  field: SourceField;
  value: string | boolean;
  onChange: (fieldName: string, value: string | boolean) => void;
  employees: FieldsFormStepProps['employees'];
}

const FormField: React.FC<FormFieldProps> = ({ field, value, onChange, employees }) => {
  const { t, language } = useLanguage();
  const label = language === 'ru' ? field.label_ru : field.label_en;

  switch (field.type) {
    // Текстовое поле или телефон
    case 'text':
    case 'tel':
      return (
        <div className="mb-4">
          <label className="block text-sm font-medium text-text-primary mb-2">
            {label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <input
            type={field.type}
            value={value as string}
            onChange={(e) => onChange(field.name, e.target.value)}
            className="w-full px-4 py-2 border border-background-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
            placeholder={label}
            required={field.required}
          />
        </div>
      );

    // Поле промокода с автоматическим uppercase
    case 'promo':
      return (
        <div className="mb-4">
          <label className="block text-sm font-medium text-text-primary mb-2">
            {label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <input
            type="text"
            value={value as string}
            onChange={(e) => onChange(field.name, e.target.value.toUpperCase())}
            className="w-full px-4 py-2 border border-background-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all font-mono"
            placeholder={label}
            required={field.required}
          />
        </div>
      );

    // Группа кнопок-переключателей
    case 'buttons':
      return (
        <div className="mb-4">
          <label className="block text-sm font-medium text-text-primary mb-2">
            {label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <div className="flex flex-wrap gap-2">
            {field.options?.map((option) => {
              const optionLabel = language === 'ru' ? option.label_ru : option.label_en;
              const isSelected = value === option.value;

              return (
                <button
                  key={String(option.value)}
                  type="button"
                  onClick={() => onChange(field.name, option.value)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    isSelected
                      ? 'bg-primary-500 text-white shadow-md'
                      : 'bg-background-200 text-text-secondary hover:bg-background-300'
                  }`}
                >
                  {optionLabel}
                </button>
              );
            })}
          </div>
        </div>
      );

    // Выпадающий список
    case 'select':
      return (
        <div className="mb-4">
          <label className="block text-sm font-medium text-text-primary mb-2">
            {label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <select
            value={value as string}
            onChange={(e) => onChange(field.name, e.target.value)}
            className="w-full px-4 py-2 border border-background-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
            required={field.required}
          >
            <option value="">{t('addOrder.selectOption')}</option>
            {/* Загрузка списка сотрудников, если указано loadOptions */}
            {field.loadOptions === '/api/employees' &&
              employees.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.name}
                </option>
              ))}
          </select>
        </div>
      );

    default:
      return null;
  }
};

export const FieldsFormStep: React.FC<FieldsFormStepProps> = ({
  selectedSource,
  formData,
  employees,
  onFieldChange,
  onBack,
  onSubmit,
  isLoading,
}) => {
  const { t, language } = useLanguage();

  // Получаем конфигурацию полей для выбранного источника
  const sourceConfig = SOURCE_FIELDS[selectedSource];
  const sourceLabel = ORDER_SOURCES.find((s) => s.value === selectedSource);
  const sourceLabelText = sourceLabel
    ? language === 'ru'
      ? sourceLabel.label_ru
      : sourceLabel.label_en
    : '';

  /**
   * Проверяет, нужно ли показывать поле
   * Используется для условных полей (например, адрес показывается только при выборе доставки)
   */
  const shouldShowField = (field: SourceField): boolean => {
    if (!field.showIf) return true;
    return formData[field.showIf.field] === field.showIf.value;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <div className="min-h-screen bg-background-100 py-8">
      <div className="container-app max-w-2xl">
        {/* Заголовок с кнопкой "Назад" */}
        <div className="mb-6">
          <button
            onClick={onBack}
            className="flex items-center text-text-secondary hover:text-primary-500 transition-colors mb-4"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M15 19l-7-7 7-7" />
            </svg>
            {t('addOrder.backToSource')}
          </button>
          <h1 className="text-2xl font-bold text-text-primary mb-2">
            {t('addOrder.fillFieldsTitle')}: {sourceLabelText}
          </h1>
          <p className="text-text-secondary">{t('addOrder.fillFieldsSubtitle')}</p>
        </div>

        {/* Форма */}
        <div className="card">
          <form onSubmit={handleSubmit}>
            {/* Рендерим поля динамически */}
            {sourceConfig?.fields.map((field) =>
              shouldShowField(field) ? (
                <FormField
                  key={field.name}
                  field={field}
                  value={formData[field.name] || ''}
                  onChange={onFieldChange}
                  employees={employees}
                />
              ) : null
            )}

            {/* Кнопки действий */}
            <div className="flex flex-col sm:flex-row gap-3 mt-6 pt-6 border-t border-background-300">
              <button
                type="button"
                onClick={onBack}
                className="btn btn-secondary flex-1"
                disabled={isLoading}
              >
                {t('addOrder.cancel')}
              </button>
              <button
                type="submit"
                className="btn btn-primary flex-1"
                disabled={isLoading}
              >
                {isLoading ? t('addOrder.loadingMenu') : t('addOrder.continue')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
